import { Box, Button, Heading } from '@hover/blueprint';
import React, { useContext } from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router';
import type { InferType } from 'yup';

import { makeSignupValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import { LinkExternal } from 'src/shared/components';
import {
  CheckboxField,
  Form as SharedForm,
  TextField,
} from 'src/shared/components/forms';
import { useOnboardingCreateUserMutation } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useTranslate,
} from 'src/shared/hooks';
import { External, Route as Routes } from 'src/shared/route';
import { buttonPressed, pageViewed } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';

import { OnboardingContext } from './OnboardingContext';

export type SignupFormData = InferType<
  ReturnType<typeof makeSignupValidationSchema>
>;

export const Signup = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const history = useHistory();
  const [createUser] = useOnboardingCreateUserMutation();
  const [_state, dispatch] = useContext(OnboardingContext);

  const validationSchema = makeSignupValidationSchema(t);
  const initialValues = { email: '', agreeToTerms: false };

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Onboarding-signup-email_input',
      feature: 'onboarding',
    });
  });

  const onSubmit = async (formData: SignupFormData) => {
    const { email, agreeToTerms } = formData;
    if (!email || !agreeToTerms) return;

    dispatch({ email });

    try {
      await createUser({
        variables: {
          createUserArgs: {
            user: {
              email,
            },
            withMagicLink: true,
          },
        },
      });
    } catch (e) {
      logError(e as Error);
    } finally {
      history.push(`${Routes.SIGN_UP_ONBOARDING.path}/check`);
    }
  };

  return (
    <Box alignItems="center" height="100vh" justifyContent="center" mx={500}>
      <Box flexDirection="column">
        <Heading
          color="neutral700"
          fontWeight="headingLight"
          size={{ base: 700, tablet: 900 }}
        >
          {t('onboarding.signupWithEmail')}
        </Heading>

        <Box flexDirection="column" mt={200}>
          <SharedForm<SignupFormData>
            display="flex"
            height="100%"
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
          >
            <TextField
              name="email"
              placeholder={t('onboarding.email')}
              type="email"
              width={{ base: 1, tablet: 'auto' }}
            />

            <CheckboxField ariaLabel={t('termsAgreement')} name="agreeToTerms">
              <Trans
                components={{
                  TermsOfServiceLink: (
                    <LinkExternal href={External.TERMS_OF_SERVICE.path} />
                  ),
                  PrivacyPolicyLink: (
                    <LinkExternal href={External.PRIVACY_POLICY.path} />
                  ),
                }}
                i18nKey="byCreatingAnAccount"
                t={t}
              />
            </CheckboxField>

            <Button
              mt={300}
              onClick={() => {
                buttonPressed({
                  button_text: 'Next',
                  page_or_screen_name: 'Onboarding-signup-email_input',
                  feature: 'onboarding',
                });
              }}
              type="submit"
            >
              {t('onboarding.next')}
            </Button>
          </SharedForm>
        </Box>
      </Box>
    </Box>
  );
};
